import {
  Badge,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import type { MessageKey } from "../../shared/i18n";
import { endpoints, type OrganizationSummary } from "../api/client";
import { organizationsQuery, useNotifyError } from "../api/queries";
import { AppFrame } from "../components/app-frame";
import { EmptyState } from "../components/empty-state";
import { Page, PageHeader } from "../components/page";
import { useMessages } from "../i18n/provider";
import styles from "./organizations.module.css";

export function OrganizationsPage() {
  const t = useMessages();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const { data, isLoading } = useQuery(organizationsQuery);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => endpoints.createOrganization({ name: name.trim() }),
    onSuccess: async (organization) => {
      await queryClient.invalidateQueries({ queryKey: organizationsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      setCreateOpen(false);
      setName("");
      navigate(`/organization/${organization.id}`);
    },
    onError: notifyError,
  });

  const items = data?.items ?? [];

  return (
    <AppFrame active="/organization">
      <Page>
        <PageHeader
          title={t("organization.title")}
          description={t("organization.subtitle")}
          action={
            <Button
              leftSection={<IconPlus size={16} stroke={2} />}
              onClick={() => setCreateOpen(true)}
            >
              {t("organization.create")}
            </Button>
          }
        />

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <Skeleton height={132} radius="lg" />
            <Skeleton height={132} radius="lg" />
          </SimpleGrid>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("organization.emptyTitle")}
            body={t("organization.emptyBody")}
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                {t("organization.create")}
              </Button>
            }
          />
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {items.map((organization) => (
              <OrganizationCard key={organization.id} organization={organization} />
            ))}
          </SimpleGrid>
        )}
      </Page>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("organization.createTitle")}
        size="sm"
      >
        <Stack>
          <TextInput
            label={t("organization.name")}
            placeholder={t("organization.namePlaceholder")}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            maxLength={80}
            data-autofocus
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={name.trim().length < 2}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppFrame>
  );
}

function OrganizationCard({ organization }: { organization: OrganizationSummary }) {
  const t = useMessages();
  const active = organization.plan === "pro";

  return (
    <Link to={`/organization/${organization.id}`} className={styles.card}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div>
          <Text fw={600} size="md">
            {organization.name}
          </Text>
          <Text size="sm" c="dimmed" mt={2}>
            {t(`organization.role.${organization.role}` as MessageKey)} ·{" "}
            {t("organization.members", { count: organization.memberCount })}
          </Text>
        </div>
        <Badge variant="light" color={active ? "blue" : "gray"} size="sm">
          {active ? t("organization.planActive") : t("organization.planInactive")}
        </Badge>
      </Group>
      <Group justify="space-between" align="end" mt="lg">
        <Text size="xs" c="dimmed">
          {t("organization.seatCount")}: {organization.seats || "—"}
        </Text>
        <Text size="sm" fw={500}>
          {t("organization.open")} →
        </Text>
      </Group>
    </Link>
  );
}
